import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, ArrowRight, Sparkles, Brain, Search, Filter, Zap, Feather,
  DollarSign, Ruler, Layers, Target, Rocket, FlaskConical, Compass, Cpu, Wand2, Smile
} from "lucide-react";
import Logo from "../components/Logo";
import api from "../lib/api";
import { MODELS } from "../components/ModelBadge";

/* -------------------- When-to-use recipes -------------------- */
const RECIPES = [
  {
    q: "Best writing & nuance",
    a: "claude-sonnet-4-6",
    why: "Anthropic's Sonnet 4.6 has the strongest prose voice and most careful tone — the only choice for external-facing copy, delicate memos, and long-form.",
    icon: Feather, color: "#D97706",
  },
  {
    q: "Cheapest capable brain",
    a: "gemini-2-5-flash-lite",
    why: "Sub-cent inputs and outputs with a 1M-token window. Perfect for bulk summarisation and inbox triage.",
    icon: Zap, color: "#4285F4",
  },
  {
    q: "Math, code & analytics",
    a: "deepseek-chat",
    why: "DeepSeek V3 punches far above its price on STEM benchmarks. First choice for numeric, quantitative, or code-heavy tasks.",
    icon: Cpu, color: "#3B82F6",
  },
  {
    q: "Very long documents",
    a: "gemini-2-5-pro",
    why: "2M-token context beats every other brain in the catalog. Feed it a whole codebase or a stack of PDFs.",
    icon: Compass, color: "#4285F4",
  },
  {
    q: "Deep reasoning & tools",
    a: "gpt-5-1",
    why: "OpenAI's flagship. Best step-by-step reasoning and most reliable agentic tool use. Reach here for the important calls.",
    icon: Brain, color: "#10A37F",
  },
  {
    q: "Creative & narrative work",
    a: "kimi-k2",
    why: "Kimi K2 has a lyrical, associative voice with a 200k window — great for storytelling, narrative marketing, and creative briefs.",
    icon: Sparkles, color: "#10B981",
  },
  {
    q: "Fast, cheap defaults",
    a: "gpt-4o-mini",
    why: "GPT-4o Mini is the reliable everyday driver. Uses tools, sees images, replies quickly — at pennies per thousand tokens.",
    icon: Zap, color: "#10A37F",
  },
  {
    q: "Brainstorming & taglines",
    a: "qwen-72b",
    why: "Qwen is bold and terse — say more with fewer tokens. Great for punchy taglines, one-liners, and multilingual copy.",
    icon: Rocket, color: "#EF4444",
  },
  {
    q: "European data lineage",
    a: "mistral-large",
    why: "When data sovereignty matters, Mistral Large is the strongest European-lineage reasoner with solid tool use.",
    icon: FlaskConical, color: "#FA5D0F",
  },
];

const TIER_META = {
  premium:  { label: "Premium",  color: "#B45309", bg: "#FEF3C7", desc: "Frontier quality" },
  balanced: { label: "Balanced", color: "#1D4ED8", bg: "#DBEAFE", desc: "Quality × price" },
  fast:     { label: "Fast",     color: "#047857", bg: "#D1FAE5", desc: "Cheapest & fastest" },
};

const FAMILY_ORDER = ["OpenAI", "Anthropic", "Google", "DeepSeek", "Qwen", "Moonshot", "Meta", "Mistral"];

/* -------------------- Cost Simulator context -------------------- */
const CostCtx = React.createContext({ dailyPrompts: 1000, avgIn: 500, avgOut: 500 });
function projectMonthly(brain, sim) {
  const inCost = ((sim.avgIn / 1000) * (brain.cost_in || 0));
  const outCost = ((sim.avgOut / 1000) * (brain.cost_out || 0));
  return sim.dailyPrompts * 30 * (inCost + outCost);
}
function fmtMonthly(v) {
  if (v == null || isNaN(v)) return "—";
  if (v < 0.01) return `<$0.01`;
  if (v < 1) return `$${v.toFixed(2)}`;
  if (v < 100) return `$${v.toFixed(1)}`;
  return `$${Math.round(v).toLocaleString()}`;
}

/* -------------------- Models Page -------------------- */
export default function ModelsPage() {
  const [brains, setBrains] = useState([]);
  const [q, setQ] = useState("");
  const [tier, setTier] = useState("all");
  const [focus, setFocus] = useState(null); // brain id for the detail modal
  const [dailyPrompts, setDailyPrompts] = useState(1000);
  const [avgIn, setAvgIn] = useState(500);
  const [avgOut, setAvgOut] = useState(500);
  const sim = useMemo(() => ({ dailyPrompts, avgIn, avgOut }), [dailyPrompts, avgIn, avgOut]);

  useEffect(() => {
    api.get("/models").then(({ data }) => setBrains(data.models || []));
  }, []);

  const filtered = brains.filter((b) => {
    if (tier !== "all" && b.tier !== tier) return false;
    if (q) {
      const s = (b.name + " " + b.family + " " + (b.strengths || []).join(" ") + " " + (b.when_to_use || "")).toLowerCase();
      if (!s.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  const grouped = FAMILY_ORDER.map((fam) => ({
    family: fam,
    brains: filtered.filter((b) => b.family === fam),
  })).filter((g) => g.brains.length > 0);

  return (
    <div className="min-h-screen bg-[#FDF8F5]" data-testid="models-page">
      {/* Header */}
      <header className="border-b-2 border-slate-900 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="brand" className="flex items-center gap-2 text-slate-900 hover:opacity-80">
            <Logo size={32} wordmark="full" tagline={false} />
          </Link>
          <div className="flex items-center gap-4 text-sm font-heading font-bold">
            <Link to="/about" className="text-slate-600 hover:text-slate-900" data-testid="nav-about">About</Link>
            <Link to="/login" className="brut-btn text-sm" data-testid="nav-signin">
              Sign in <ArrowRight size={14} strokeWidth={2.5} />
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-12 md:py-16" data-testid="models-hero">
        <Link to="/" className="text-sm font-heading font-bold text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5 mb-6" data-testid="back-link">
          <ArrowLeft size={14} strokeWidth={2.5} /> Back to home
        </Link>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-200 border-2 border-slate-900 font-heading font-semibold text-xs uppercase tracking-widest mb-5">
          <Brain size={12} strokeWidth={3} />
          The catalog · 12 brains · 8 providers
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] tracking-tight max-w-3xl mb-4">
          Which brain, <span className="bg-amber-200 px-2 -rotate-1 inline-block border-2 border-slate-900 rounded-lg">when</span>?
        </h1>
        <p className="text-lg text-slate-600 max-w-2xl leading-relaxed">
          Every AiMedley worker, workflow node, and Quick Agent can use any of the twelve frontier brains below. This page is the cheat sheet — pick by strength, tier, or price, and you'll never send a legal memo to a math model again.
        </p>
      </section>

      {/* When-to-use recipes */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-14" data-testid="recipes-section">
        <div className="flex items-baseline justify-between mb-5">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Quick picks — start here</h2>
          <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-500 hidden sm:block">If you need… → use this brain</div>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="recipes-grid">
          {RECIPES.map((r, i) => {
            const b = brains.find((x) => x.id === r.a);
            const RIcon = r.icon;
            return (
              <button
                key={i}
                onClick={() => setFocus(r.a)}
                className="brut-card p-5 text-left hover:-translate-y-0.5 transition-transform"
                style={{ borderColor: r.color, boxShadow: `4px 4px 0 ${r.color}` }}
                data-testid={`recipe-${r.a}`}
              >
                <div className="flex items-center gap-2 mb-2.5">
                  <div className="w-9 h-9 rounded-lg border-2 border-slate-900 flex items-center justify-center" style={{ background: r.color + "22", color: r.color }}>
                    <RIcon size={16} strokeWidth={2.5} />
                  </div>
                  <div className="text-xs uppercase tracking-widest font-heading font-bold" style={{ color: r.color }}>Recipe</div>
                </div>
                <div className="font-heading font-bold text-lg mb-1">{r.q}</div>
                <div className="text-sm font-heading font-bold text-slate-900 mb-2">
                  → {b?.name || r.a}
                  <span className="ml-1 text-[10px] uppercase tracking-widest text-slate-400">{b?.family}</span>
                </div>
                <p className="text-sm text-slate-600 leading-relaxed">{r.why}</p>
              </button>
            );
          })}
        </div>
      </section>

      {/* Quiz */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-14" data-testid="quiz-section">
        <BrainQuiz brains={brains} />
      </section>

      {/* Model Playground */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pb-14" data-testid="playground-section">
        <Playground brains={brains} />
      </section>

      {/* Filters */}
      <section className="max-w-7xl mx-auto px-6 md:px-10" data-testid="catalog-toolbar">
        <div className="brut-card p-4 md:p-5 flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 flex-1 min-w-[240px]">
            <Search size={16} strokeWidth={2.5} className="text-slate-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, strength, or use-case (e.g. code, prose, cheap)…"
              className="brut-input py-1.5 text-sm"
              data-testid="brain-search"
            />
          </div>
          <div className="flex items-center gap-2">
            <Filter size={14} strokeWidth={2.5} className="text-slate-400" />
            <span className="text-xs uppercase tracking-widest font-heading font-bold text-slate-500">Tier</span>
            {["all", "premium", "balanced", "fast"].map((t) => (
              <button
                key={t}
                onClick={() => setTier(t)}
                className={`px-3 py-1 rounded-full text-xs font-heading font-bold border-2 ${
                  tier === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-900"
                }`}
                data-testid={`filter-${t}`}
              >
                {t === "all" ? "All" : TIER_META[t].label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Cost Simulator */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-6" data-testid="cost-sim">
        <div className="brut-card p-4 md:p-5 bg-amber-50" style={{ borderColor: "#B45309", boxShadow: "5px 5px 0 #B45309" }}>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-amber-700 mb-0.5">Cost Simulator</div>
              <div className="font-heading font-bold text-sm">See monthly cost per brain — updates live below</div>
            </div>
            <SimField label="Daily prompts" value={dailyPrompts} onChange={setDailyPrompts} min={10} max={100000} step={100} testid="sim-daily" />
            <SimField label="Avg input tokens"  value={avgIn}  onChange={setAvgIn}  min={50} max={20000} step={50}  testid="sim-in" />
            <SimField label="Avg output tokens" value={avgOut} onChange={setAvgOut} min={50} max={20000} step={50}  testid="sim-out" />
            <button
              onClick={() => { setDailyPrompts(1000); setAvgIn(500); setAvgOut(500); }}
              className="ml-auto text-[10px] uppercase tracking-widest font-heading font-bold text-slate-600 hover:text-slate-900 underline underline-offset-2"
              data-testid="sim-reset"
            >
              Reset
            </button>
          </div>
        </div>
      </section>

      {/* Catalog grouped by family */}
      <CostCtx.Provider value={sim}>
      <section className="max-w-7xl mx-auto px-6 md:px-10 py-10 space-y-10" data-testid="catalog-section">
        {grouped.length === 0 && (
          <div className="text-center text-slate-400 py-16 text-sm">No brains match — try clearing filters.</div>
        )}
        {grouped.map(({ family, brains: fbs }) => (
          <div key={family} data-testid={`family-${family.toLowerCase()}`}>
            <div className="flex items-baseline gap-3 mb-4">
              <h3 className="font-heading text-xl md:text-2xl font-bold tracking-tight">{family}</h3>
              <span className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-400">{fbs.length} brain{fbs.length > 1 ? "s" : ""}</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {fbs.map((b) => <BrainCard key={b.id} brain={b} onOpen={() => setFocus(b.id)} />)}
            </div>
          </div>
        ))}
      </section>
      </CostCtx.Provider>

      {/* Bottom CTA */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-16">
        <div className="brut-card p-8 md:p-10 text-center text-white" style={{ background: "#0F172A", borderColor: "#0F172A", boxShadow: "6px 6px 0 #B45309" }}>
          <h2 className="font-heading text-2xl md:text-3xl font-bold mb-3">
            Pick the perfect brain in <span className="bg-amber-300 text-slate-900 px-2 border-2 border-amber-100 rounded-lg">seconds</span>, not weeks.
          </h2>
          <p className="text-slate-300 mb-6 max-w-xl mx-auto">
            Sign up and every worker, workflow node, and Quick Agent gets the full 12-brain catalog — no extra keys, no extra bills.
          </p>
          <Link to="/login" className="brut-btn bg-amber-300 text-slate-900 border-amber-100 hover:bg-amber-200 inline-flex" style={{ boxShadow: "4px 4px 0 #FFFFFF" }} data-testid="models-cta">
            <Sparkles size={14} strokeWidth={3} /> Start free · 4 workers pre-seeded
          </Link>
        </div>
      </section>

      {/* Detail modal */}
      {focus && (
        <BrainModal
          brain={brains.find((x) => x.id === focus)}
          onClose={() => setFocus(null)}
        />
      )}

      <footer className="px-6 py-8 text-center text-xs text-slate-400 font-semibold">
        AiMedley · The catalog · {new Date().getFullYear()}
      </footer>
    </div>
  );
}

/* -------------------- Sim field -------------------- */
function SimField({ label, value, onChange, min, max, step, testid }) {
  return (
    <label className="flex items-center gap-2">
      <span className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-600">{label}</span>
      <input
        type="number"
        min={min} max={max} step={step}
        value={value}
        onChange={(e) => {
          const n = parseInt(e.target.value || String(min), 10);
          onChange(Math.max(min, Math.min(max, isNaN(n) ? min : n)));
        }}
        className="brut-input py-1 px-2 text-sm w-24 text-right font-mono"
        data-testid={testid}
      />
    </label>
  );
}

/* -------------------- Brain Card -------------------- */
function BrainCard({ brain, onOpen }) {
  const local = MODELS[brain.id] || {};
  const Icon = local.icon || Brain;
  const t = TIER_META[brain.tier] || TIER_META.balanced;
  const sim = React.useContext(CostCtx);
  const monthly = projectMonthly(brain, sim);
  return (
    <button
      onClick={onOpen}
      data-testid={`brain-card-${brain.id}`}
      className="brut-card p-5 text-left hover:-translate-y-0.5 transition-transform relative"
      style={{ borderColor: brain.color, boxShadow: `4px 4px 0 ${brain.color}` }}
    >
      <div className="flex items-center gap-3 mb-3">
        <div className="w-11 h-11 rounded-lg border-2 border-slate-900 flex items-center justify-center" style={{ background: brain.color + "22", color: brain.color }}>
          <Icon size={18} strokeWidth={2.5} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-heading font-bold text-base leading-tight truncate">{brain.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">{brain.family}</div>
        </div>
        <span className="text-[10px] uppercase tracking-widest font-heading font-bold px-2 py-1 rounded-full border-2" style={{ borderColor: t.color, color: t.color, background: t.bg }}>
          {t.label}
        </span>
      </div>

      <p className="text-sm text-slate-600 leading-relaxed mb-3 line-clamp-3">{brain.when_to_use || brain.blurb}</p>

      <div className="flex flex-wrap gap-1.5 mb-3">
        {(brain.strengths || []).slice(0, 3).map((s, i) => (
          <span key={i} className="text-[10px] uppercase tracking-widest font-heading font-bold px-2 py-0.5 rounded-full border border-slate-300 bg-slate-50 text-slate-600">
            {s}
          </span>
        ))}
      </div>

      {/* Monthly projection strip */}
      <div className="rounded-lg border-2 border-slate-900 bg-amber-50 px-3 py-2 mb-2 flex items-center justify-between" data-testid={`monthly-${brain.id}`}>
        <span className="text-[10px] uppercase tracking-widest font-heading font-bold text-amber-700">Projected / mo</span>
        <span className="font-heading font-bold text-slate-900 text-base">{fmtMonthly(monthly)}</span>
      </div>

      <div className="flex items-center gap-3 text-[11px] font-mono text-slate-500 border-t-2 border-slate-100 pt-2.5">
        <span title="Context window" className="flex items-center gap-1">
          <Ruler size={11} strokeWidth={2.5} /> {fmtContext(brain.context)}
        </span>
        <span title="Input / Output $ per 1K tokens" className="flex items-center gap-1">
          <DollarSign size={11} strokeWidth={2.5} />
          {fmtCost(brain.cost_in)} / {fmtCost(brain.cost_out)}
        </span>
      </div>
    </button>
  );
}

/* -------------------- Detail Modal -------------------- */
function BrainModal({ brain, onClose }) {
  if (!brain) return null;
  const local = MODELS[brain.id] || {};
  const Icon = local.icon || Brain;
  const t = TIER_META[brain.tier] || TIER_META.balanced;

  return (
    <div
      className="fixed inset-0 bg-slate-900/60 flex items-center justify-center p-4 z-50"
      onClick={onClose}
      data-testid="brain-modal"
    >
      <div
        className="brut-card p-6 md:p-8 max-w-2xl w-full bg-white max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        style={{ borderColor: brain.color, boxShadow: `6px 6px 0 ${brain.color}` }}
      >
        <div className="flex items-start gap-4 mb-5">
          <div className="w-14 h-14 rounded-xl border-2 border-slate-900 flex items-center justify-center shrink-0" style={{ background: brain.color + "22", color: brain.color }}>
            <Icon size={24} strokeWidth={2.5} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-2xl font-bold leading-tight">{brain.name}</div>
            <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-500 mt-0.5">
              {brain.family} · <span style={{ color: t.color }}>{t.label} tier</span>
            </div>
            <div className="text-xs text-slate-500 mt-1 font-mono">{brain.id}</div>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-900 font-heading font-bold text-sm" data-testid="close-modal">
            ✕
          </button>
        </div>

        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 mb-1.5">When to use</div>
          <p className="text-slate-700 leading-relaxed">{brain.when_to_use || brain.blurb}</p>
        </div>

        <div className="mb-5">
          <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 mb-2">Strengths</div>
          <div className="flex flex-wrap gap-1.5">
            {(brain.strengths || []).map((s, i) => (
              <span key={i} className="text-xs font-heading font-bold px-3 py-1 rounded-full border-2" style={{ borderColor: brain.color, color: brain.color, background: brain.color + "11" }}>
                {s}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3 mb-5">
          <ModalStat icon={Ruler} label="Context window" value={fmtContext(brain.context)} />
          <ModalStat icon={Layers} label="Capabilities" value={(brain.capabilities || []).join(", ") || "chat"} />
          <ModalStat icon={DollarSign} label="Input cost" value={`$${fmtCost(brain.cost_in)} / 1K tokens`} />
          <ModalStat icon={DollarSign} label="Output cost" value={`$${fmtCost(brain.cost_out)} / 1K tokens`} />
        </div>

        <div className="rounded-lg bg-amber-50 border-2 border-slate-900 p-3 text-xs text-slate-700 flex items-start gap-2">
          <Target size={14} strokeWidth={3} className="text-amber-700 mt-0.5 shrink-0" />
          <div>
            <b>How to use:</b> pick this brain when creating a worker, override it on any workflow node, or select it in a Debate. Your worker's persona stays the same — only the brain changes.
          </div>
        </div>

        <CloneAsWorkflowBtn brainId={brain.id} />
      </div>
    </div>
  );
}

function CloneAsWorkflowBtn({ brainId }) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const clone = async () => {
    const token = localStorage.getItem("token");
    if (!token) { window.location.href = "/login"; return; }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post(`/workflow_templates/from_brain/${brainId}`);
      window.location.href = `/workforce/workflows/${data.workflow_id}`;
    } catch (e) {
      setErr(e.response?.data?.detail || e.message || "Clone failed");
    } finally { setBusy(false); }
  };
  return (
    <div className="mt-3">
      <button
        onClick={clone}
        disabled={busy}
        className="w-full brut-btn text-sm"
        data-testid="clone-workflow-btn"
      >
        {busy ? "Creating…" : "→ Clone as one-click workflow"}
      </button>
      {err && <div className="text-[11px] text-red-600 mt-1.5" data-testid="clone-err">{err}</div>}
    </div>
  );
}

function ModalStat({ icon: I, label, value }) {
  return (
    <div className="brut-card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 mb-1">
        <I size={11} strokeWidth={2.5} /> {label}
      </div>
      <div className="text-sm font-heading font-bold text-slate-900 break-words">{value}</div>
    </div>
  );
}

/* -------------------- helpers -------------------- */
function fmtContext(n) {
  if (!n) return "—";
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n % 1_000_000 === 0 ? 0 : 1)}M`;
  if (n >= 1000) return `${Math.round(n / 1000)}K`;
  return String(n);
}
function fmtCost(v) {
  if (v == null) return "—";
  if (v < 0.001) return v.toFixed(4);
  if (v < 0.01) return v.toFixed(4);
  return v.toFixed(3);
}

/* ============================================================
   BRAIN QUIZ — 5 questions → recommended brain
   ============================================================ */
const QUIZ = [
  {
    q: "What's the primary task?",
    options: [
      { label: "Long-form writing / prose",     tags: { writing: 3, nuance: 2 } },
      { label: "Math, code, or analytics",      tags: { code: 3, reasoning: 2 } },
      { label: "Brainstorming / ideation",      tags: { creative: 3, speed: 1 } },
      { label: "Reading long documents",        tags: { longcontext: 3 } },
      { label: "Everyday chat / drafting",      tags: { speed: 3, cheap: 2 } },
    ],
  },
  {
    q: "How important is cost?",
    options: [
      { label: "Cheapest that works",           tags: { cheap: 3 } },
      { label: "Balanced — quality × price",    tags: { balanced: 3 } },
      { label: "Money's fine — quality first",  tags: { premium: 3 } },
    ],
  },
  {
    q: "Do you need to process very long inputs?",
    options: [
      { label: "Yes — whole PDFs or codebases", tags: { longcontext: 3 } },
      { label: "Sometimes long",                tags: { longcontext: 1 } },
      { label: "Short inputs only",             tags: { speed: 1 } },
    ],
  },
  {
    q: "Do you care about data lineage?",
    options: [
      { label: "European / non-US preferred",   tags: { european: 3 } },
      { label: "Open-weight preferred",         tags: { openweight: 2 } },
      { label: "No strong preference",          tags: {} },
    ],
  },
  {
    q: "How critical is the answer?",
    options: [
      { label: "Life-or-death (contracts, launches)", tags: { premium: 3, reasoning: 2 } },
      { label: "Important — needs to be solid",       tags: { balanced: 2 } },
      { label: "Casual / high-volume",                tags: { speed: 2, cheap: 2 } },
    ],
  },
];

const BRAIN_TAGS = {
  "gpt-5-1":              { reasoning: 3, premium: 3, writing: 1, code: 2 },
  "gpt-4o-mini":          { speed: 3, cheap: 3, balanced: 1 },
  "claude-sonnet-4-6":    { writing: 3, nuance: 3, premium: 2, reasoning: 2 },
  "claude-haiku-4-5":     { writing: 2, speed: 3, cheap: 2 },
  "gemini-2-5-pro":       { longcontext: 3, premium: 2, reasoning: 2 },
  "gemini-2-5-flash-lite":{ cheap: 3, speed: 3, longcontext: 2 },
  "deepseek-chat":        { code: 3, reasoning: 3, balanced: 2 },
  "qwen-72b":             { creative: 2, speed: 2, balanced: 1 },
  "kimi-k2":              { creative: 3, writing: 2, longcontext: 2 },
  "llama-4-maverick":     { openweight: 3, longcontext: 2, premium: 1 },
  "llama-3-3-70b":        { openweight: 3, balanced: 2, cheap: 1 },
  "mistral-large":        { european: 3, balanced: 2, speed: 1 },
};

function BrainQuiz({ brains }) {
  const [step, setStep] = React.useState(0);
  const [answers, setAnswers] = React.useState([]);
  const [showResult, setShowResult] = React.useState(false);

  const pick = (opt) => {
    const next = [...answers, opt];
    setAnswers(next);
    if (next.length >= QUIZ.length) setShowResult(true);
    else setStep((s) => s + 1);
  };
  const reset = () => { setAnswers([]); setStep(0); setShowResult(false); };

  const winner = React.useMemo(() => {
    if (!showResult || brains.length === 0) return null;
    // Aggregate tag weights from picks
    const weights = {};
    for (const a of answers) {
      for (const [tag, w] of Object.entries(a.tags || {})) {
        weights[tag] = (weights[tag] || 0) + w;
      }
    }
    // Score each brain
    let bestId = null, bestScore = -Infinity;
    for (const b of brains) {
      const bt = BRAIN_TAGS[b.id] || {};
      let score = 0;
      for (const [tag, bw] of Object.entries(bt)) {
        score += (weights[tag] || 0) * bw;
      }
      if (score > bestScore) { bestScore = score; bestId = b.id; }
    }
    return brains.find((b) => b.id === bestId) || null;
  }, [showResult, answers, brains]);

  return (
    <div className="brut-card p-6 md:p-8 bg-white" style={{ borderColor: "#0F172A", boxShadow: "6px 6px 0 #B45309" }}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg border-2 border-slate-900 bg-amber-200 flex items-center justify-center">
          <Sparkles size={16} strokeWidth={2.5} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-amber-700">30-second quiz</div>
          <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight">Which AI should I use?</h2>
        </div>
        {(showResult || answers.length > 0) && (
          <button onClick={reset} className="ml-auto text-[10px] uppercase tracking-widest font-heading font-bold text-slate-600 hover:text-slate-900 underline underline-offset-2" data-testid="quiz-reset">
            Restart
          </button>
        )}
      </div>

      {!showResult ? (
        <div data-testid="quiz-active">
          <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 mb-2">
            Question {step + 1} of {QUIZ.length}
          </div>
          <div className="font-heading font-bold text-lg mb-4">{QUIZ[step].q}</div>
          <div className="space-y-2">
            {QUIZ[step].options.map((o, i) => (
              <button
                key={i}
                onClick={() => pick(o)}
                className="w-full text-left px-4 py-3 rounded-xl border-2 border-slate-900 bg-white hover:bg-amber-50 font-heading font-semibold text-sm transition-colors"
                data-testid={`quiz-opt-${step}-${i}`}
              >
                {o.label}
              </button>
            ))}
          </div>
          <div className="mt-4 h-1.5 rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-amber-500 transition-all" style={{ width: `${((step) / QUIZ.length) * 100}%` }} />
          </div>
        </div>
      ) : (
        <div data-testid="quiz-result">
          <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-emerald-700 mb-2">Your match</div>
          {winner ? (
            <>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-14 h-14 rounded-xl border-2 border-slate-900 flex items-center justify-center" style={{ background: winner.color + "22", color: winner.color }}>
                  <Brain size={24} strokeWidth={2.5} />
                </div>
                <div>
                  <div className="font-heading font-bold text-2xl">{winner.name}</div>
                  <div className="text-xs uppercase tracking-widest font-heading font-bold text-slate-500">{winner.family} · {winner.tier}</div>
                </div>
              </div>
              <p className="text-slate-700 leading-relaxed mb-4">{winner.when_to_use || winner.blurb}</p>
              <CloneAsWorkflowBtn brainId={winner.id} />
            </>
          ) : (
            <div className="text-slate-500">Couldn't compute a match — try again.</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ============================================================
   PLAYGROUND — public race across 2-3 brains, no auth required
   ============================================================ */
function Playground({ brains }) {
  const [selected, setSelected] = React.useState(["gpt-4o-mini", "claude-haiku-4-5", "gemini-2-5-flash-lite"]);
  const [prompt, setPrompt] = React.useState("Explain quantum entanglement in 3 sentences a curious 10-year-old would love.");
  const [running, setRunning] = React.useState(false);
  const [streams, setStreams] = React.useState({}); // brain_id → {content, done, cost, error}
  const abortRef = React.useRef([]);

  const toggle = (id) => {
    if (running) return;
    setSelected((cur) => {
      if (cur.includes(id)) return cur.filter((x) => x !== id);
      if (cur.length >= 3) return cur;
      return [...cur, id];
    });
  };

  const run = async () => {
    if (running || selected.length < 2 || !prompt.trim()) return;
    setRunning(true);
    setStreams(Object.fromEntries(selected.map((id) => [id, { content: "", done: false }])));
    const API_BASE = process.env.REACT_APP_BACKEND_URL;

    const race = selected.map(async (id) => {
      const ctrl = new AbortController();
      abortRef.current.push(ctrl);
      try {
        const res = await fetch(`${API_BASE}/api/playground/stream`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ brain_id: id, prompt }),
          signal: ctrl.signal,
        });
        if (!res.ok) {
          const err = await res.text().catch(() => "");
          setStreams((s) => ({ ...s, [id]: { ...s[id], done: true, error: err || `HTTP ${res.status}` } }));
          return;
        }
        const reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });
          const parts = buffer.split("\n\n");
          buffer = parts.pop();
          for (const chunk of parts) {
            const line = chunk.trim();
            if (!line.startsWith("data:")) continue;
            const raw = line.slice(5).trim();
            if (raw === "[DONE]") continue;
            try {
              const ev = JSON.parse(raw);
              if (ev.type === "delta") {
                setStreams((s) => ({ ...s, [id]: { ...s[id], content: (s[id]?.content || "") + ev.content } }));
              } else if (ev.type === "done") {
                setStreams((s) => ({ ...s, [id]: { ...s[id], done: true, cost: ev.cost } }));
              } else if (ev.type === "error") {
                setStreams((s) => ({ ...s, [id]: { ...s[id], done: true, error: ev.error } }));
              }
            } catch (_) {}
          }
        }
      } catch (e) {
        if (e.name !== "AbortError") {
          setStreams((s) => ({ ...s, [id]: { ...s[id], done: true, error: e.message } }));
        }
      }
    });

    try { await Promise.all(race); }
    finally {
      setRunning(false);
      abortRef.current = [];
    }
  };

  const stop = () => { abortRef.current.forEach((c) => c.abort()); };

  return (
    <div className="brut-card p-6 md:p-8 bg-white" style={{ borderColor: "#0F172A", boxShadow: "6px 6px 0 #B45309" }}>
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-lg border-2 border-slate-900 bg-emerald-200 flex items-center justify-center">
          <Rocket size={16} strokeWidth={2.5} />
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-emerald-700">Try live · no signup</div>
          <h2 className="font-heading text-xl md:text-2xl font-bold tracking-tight">Race 2–3 brains, one prompt</h2>
        </div>
      </div>

      <p className="text-sm text-slate-600 leading-relaxed mb-4">
        Pick 2–3 brains, send a prompt, and watch them stream side-by-side. Public preview — limited to short prompts and 3 attempts per hour.
      </p>

      <div className="mb-3">
        <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-500 mb-1.5">Pick 2–3 brains</div>
        <div className="flex flex-wrap gap-1.5" data-testid="playground-chips">
          {brains.map((b) => {
            const on = selected.includes(b.id);
            const t = TIER_META[b.tier] || TIER_META.balanced;
            return (
              <button
                key={b.id}
                onClick={() => toggle(b.id)}
                disabled={running}
                className={`px-3 py-1.5 rounded-full text-xs font-heading font-bold border-2 transition-transform ${on ? "" : "hover:-translate-y-0.5"}`}
                style={{
                  background: on ? b.color : "#fff",
                  color: on ? "#fff" : t.color,
                  borderColor: b.color,
                  boxShadow: on ? `2px 2px 0 ${b.color}` : "none",
                }}
                data-testid={`playground-chip-${b.id}`}
              >
                {b.name}
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-3">
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={2}
          maxLength={400}
          disabled={running}
          className="brut-input text-sm"
          placeholder="Ask something interesting (max 400 chars)…"
          data-testid="playground-input"
        />
        <div className="flex items-center justify-between mt-1.5">
          <div className="text-[10px] uppercase tracking-widest font-heading font-bold text-slate-400">
            {prompt.length}/400 · {selected.length} brain{selected.length === 1 ? "" : "s"} picked
          </div>
          <div className="flex gap-2">
            {running ? (
              <button onClick={stop} className="brut-btn brut-btn-outline text-xs" data-testid="playground-stop">Stop</button>
            ) : (
              <button
                onClick={run}
                disabled={selected.length < 2 || selected.length > 3 || !prompt.trim()}
                className="brut-btn text-xs"
                data-testid="playground-run"
              >
                <PlayIcon /> Race brains
              </button>
            )}
          </div>
        </div>
      </div>

      {Object.keys(streams).length > 0 && (
        <div className={`grid gap-3 ${Object.keys(streams).length === 2 ? "md:grid-cols-2" : "md:grid-cols-3"}`} data-testid="playground-streams">
          {selected.map((id) => {
            const b = brains.find((x) => x.id === id); if (!b) return null;
            const s = streams[id] || {};
            return (
              <div key={id} className="brut-card p-3 min-h-[220px]" style={{ borderColor: b.color, boxShadow: `3px 3px 0 ${b.color}` }} data-testid={`playground-out-${id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-7 h-7 rounded-md border-2 border-slate-900 flex items-center justify-center" style={{ background: b.color + "22", color: b.color }}>
                    <Brain size={12} strokeWidth={2.5} />
                  </div>
                  <div className="font-heading font-bold text-xs truncate flex-1">{b.name}</div>
                  {s.done && s.cost != null && !s.error && (
                    <span className="text-[10px] font-mono text-slate-500">${(s.cost || 0).toFixed(4)}</span>
                  )}
                  {!s.done && running && <Loader className="animate-spin text-amber-600" />}
                </div>
                <div className="text-xs whitespace-pre-wrap leading-relaxed text-slate-700">
                  {s.error ? <span className="text-red-600">{s.error}</span> : (s.content || (running ? "…" : ""))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// tiny icons used in Playground
function PlayIcon() { return <Rocket size={12} strokeWidth={2.5} />; }
function Loader({ className = "" }) {
  return <svg className={className} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="2" x2="12" y2="6"/><line x1="12" y1="18" x2="12" y2="22"/><line x1="4.93" y1="4.93" x2="7.76" y2="7.76"/><line x1="16.24" y1="16.24" x2="19.07" y2="19.07"/><line x1="2" y1="12" x2="6" y2="12"/><line x1="18" y1="12" x2="22" y2="12"/><line x1="4.93" y1="19.07" x2="7.76" y2="16.24"/><line x1="16.24" y1="7.76" x2="19.07" y2="4.93"/></svg>;
}
